import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { isRecoverySession, signOut } = useAuth();

  // 요청 모드 (비밀번호 찾기)
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [linkError, setLinkError] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  // 복구 모드 (검증된 recovery claim을 가진 세션에서만 새 비밀번호 설정)
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    // 만료·무효 링크 감지: Supabase가 실패 시 error_code/error_description을 hash로 반환
    const hash = window.location.hash || '';
    if (hash.includes('error_code') || hash.includes('error_description')) {
      setLinkError('재설정 링크가 만료되었거나 유효하지 않습니다. 아래에서 다시 요청해주세요.');
      // 에러 fragment 정리
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);

  // recovery claim이 검증되면(세션에 이미 반영됨) URL의 토큰/error fragment를 정리한다.
  useEffect(() => {
    if (isRecoverySession && window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, [isRecoverySession]);

  const handleRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('이메일을 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      // 복구 링크는 recovery claim을 검증하는 이 페이지로 돌아온다
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (resetError) {
        setError(resetError.message);
      } else {
        setSent(true);
      }
    } catch {
      setError('비밀번호 재설정 요청 중 오류가 발생했습니다.');
    }
    setLoading(false);
  };

  const handleResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError('');

    if (!newPassword.trim()) {
      setResetError('새 비밀번호를 입력해주세요.');
      return;
    }
    if (newPassword.length < 6) {
      setResetError('비밀번호는 6자 이상이어야 합니다.');
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setResetError('비밀번호가 일치하지 않습니다.');
      return;
    }

    setResetLoading(true);
    try {
      // 유효한 복구 세션(amr.method='recovery')이 없으면 이 호출은 실패한다 (만료/무효 링크)
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        setResetError('재설정 링크가 만료되었거나 유효하지 않습니다. 아래에서 다시 요청해주세요.');
        setResetLoading(false);
        return;
      }

      // 성공: recovery 세션을 signOut해 인증 상태·복구 상태를 제거한 뒤 로그인으로 이동
      await signOut();
      navigate('/login', { replace: true });
    } catch {
      setResetError('비밀번호 재설정 중 오류가 발생했습니다. 다시 시도해주세요.');
      setResetLoading(false);
    }
  };

  // ── 복구 모드 (검증된 recovery claim) ──
  if (isRecoverySession) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 cursor-pointer">
              <div className="w-10 h-10 rounded-full bg-secondary-500 flex items-center justify-center">
                <i className="ri-flask-line text-white text-sm"></i>
              </div>
              <span className="font-heading text-2xl font-bold"><span className="text-primary-700">Lotto</span><span className="text-secondary-500">Lab</span></span>
            </Link>
          </div>

          <div className="bg-background-50 rounded-2xl p-8 border border-background-200">
            <h1 className="font-heading text-2xl text-foreground-800 mb-1">새 비밀번호 설정</h1>
            <p className="text-foreground-400 text-sm mb-6">
              이메일 링크로 인증되었습니다. 새 비밀번호를 설정해주세요.
            </p>

            {resetError && (
              <div className="mb-5 p-3 rounded-lg bg-accent-50 border border-accent-200 flex items-start gap-2">
                <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5"></i>
                <p className="text-accent-700 text-sm">{resetError}</p>
              </div>
            )}

            <form onSubmit={handleResetSubmit} className="space-y-4">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  새 비밀번호
                </label>
                <input
                  id="newPassword"
                  type="password"
                  name="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="6자 이상 입력해주세요"
                  autoComplete="new-password"
                  className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                />
              </div>
              <div>
                <label htmlFor="newPasswordConfirm" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  새 비밀번호 확인
                </label>
                <input
                  id="newPasswordConfirm"
                  type="password"
                  name="newPasswordConfirm"
                  value={newPasswordConfirm}
                  onChange={(e) => setNewPasswordConfirm(e.target.value)}
                  placeholder="새 비밀번호를 다시 입력해주세요"
                  autoComplete="new-password"
                  className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                />
              </div>

              <button
                type="submit"
                disabled={resetLoading}
                className="w-full py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {resetLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    재설정 중...
                  </span>
                ) : (
                  '비밀번호 재설정'
                )}
              </button>
            </form>

            <div className="mt-6 pt-5 border-t border-background-200 text-center">
              <p className="text-foreground-400 text-sm">
                링크가 만료되었거나 문제가 있나요?{' '}
                <button
                  onClick={async () => {
                    await signOut();
                    setSent(false);
                    setEmail('');
                    setLinkError('');
                  }}
                  className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                >
                  다시 요청하기
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── 요청 모드 ──
  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 cursor-pointer">
            <div className="w-10 h-10 rounded-full bg-secondary-500 flex items-center justify-center">
              <i className="ri-flask-line text-white text-sm"></i>
            </div>
            <span className="font-heading text-2xl font-bold"><span className="text-primary-700">Lotto</span><span className="text-secondary-500">Lab</span></span>
          </Link>
        </div>

        <div className="bg-background-50 rounded-2xl p-8 border border-background-200">
          {sent ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center">
                <i className="ri-mail-send-line text-2xl text-secondary-600"></i>
              </div>
              <h1 className="font-heading text-xl text-foreground-800 mb-2">이메일 발송 완료</h1>
              <p className="text-foreground-500 text-sm mb-4">
                <strong className="text-foreground-700">{email}</strong>로 비밀번호 재설정 링크를 발송했습니다.
                이메일을 확인하고 링크를 클릭해주세요.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-all cursor-pointer whitespace-nowrap"
              >
                로그인으로 돌아가기
              </button>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-2xl text-foreground-800 mb-1">비밀번호 찾기</h1>
              <p className="text-foreground-400 text-sm mb-6">
                가입 시 사용한 이메일을 입력하면 비밀번호 재설정 링크를 보내드려요.
              </p>

              {linkError && (
                <div className="mb-5 p-3 rounded-lg bg-accent-50 border border-accent-200 flex items-start gap-2">
                  <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5"></i>
                  <p className="text-accent-700 text-sm">{linkError}</p>
                </div>
              )}

              {error && (
                <div className="mb-5 p-3 rounded-lg bg-accent-50 border border-accent-200 flex items-start gap-2">
                  <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5"></i>
                  <p className="text-accent-700 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleRequestSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    이메일
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="hello@example.com"
                    autoComplete="email"
                    className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      전송 중...
                    </span>
                  ) : (
                    '재설정 링크 보내기'
                  )}
                </button>
              </form>

              <div className="mt-6 pt-5 border-t border-background-200 text-center">
                <p className="text-foreground-400 text-sm">
                  비밀번호가 생각나셨나요?{' '}
                  <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
                    로그인
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}